import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    Tab,
    Tabs,
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider
} from '@mui/material';
import {
    Delete,
    Instagram,
    Facebook,
    Telegram,
    WhatsApp,
    CheckCircle,
    Error as ErrorIcon,
    AccountCircle
} from '@mui/icons-material';
import { getAccounts as getInstagramAccounts } from '../redux/slices/instagramSlice';
import { getFacebookAccounts } from '../redux/slices/facebookSlice';
import { getBots as getTelegramAccounts } from '../redux/slices/telegramSlice';
import { getWhatsAppAccounts } from '../redux/slices/whatsappSlice';
import axios from 'axios';
import io from 'socket.io-client';

const AccountsManager = () => {
    const dispatch = useDispatch();
    const [currentTab, setCurrentTab] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, platform: null, accountId: null });

    // Get accounts from Redux
    const instagramAccounts = useSelector((state) => state.instagram.accounts);
    const facebookAccounts = useSelector((state) => state.facebook.accounts);
    const telegramAccounts = useSelector((state) => state.telegram.bots || []);
    const whatsappAccounts = useSelector((state) => state.whatsapp.accounts || []);
    const token = useSelector((state) => state.auth.token);

    // Form states
    const [instagramForm, setInstagramForm] = useState({ username: '', password: '' });
    const [facebookForm, setFacebookForm] = useState({ cookies: '' });
    const [telegramForm, setTelegramForm] = useState({ botToken: '', phoneNumber: '' });
    const [whatsappForm, setWhatsappForm] = useState({ phoneNumber: '' });
    const [qrDialog, setQrDialog] = useState({ open: false, qrCode: null, accountId: null });

    useEffect(() => {
        dispatch(getInstagramAccounts());
        dispatch(getFacebookAccounts());
        dispatch(getTelegramAccounts());
        dispatch(getWhatsAppAccounts());

        // Setup socket for QR codes
        const socket = io('https://social-auto-r8we.onrender.com');
        socket.on('connect', () => console.log('Socket connected'));

        return () => socket.disconnect();
    }, [dispatch]);

    const handleAddInstagram = async () => {
        if (!instagramForm.username || !instagramForm.password) {
            setSnackbar({ open: true, message: 'يرجى إدخال اسم المستخدم وكلمة المرور', severity: 'error' });
            return;
        }

        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.post('https://social-auto-r8we.onrender.com/api/instagram/accounts', instagramForm, config);
            setSnackbar({ open: true, message: '✅ تم إضافة حساب Instagram بنجاح!', severity: 'success' });
            setInstagramForm({ username: '', password: '' });
            dispatch(getInstagramAccounts());
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || 'فشل إضافة الحساب', severity: 'error' });
        }
    };

    const handleAddFacebook = async () => {
        if (!facebookForm.cookies) {
            setSnackbar({ open: true, message: 'يرجى إدخال Cookies', severity: 'error' });
            return;
        }

        try {
            const parsedCookies = JSON.parse(facebookForm.cookies);
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.post('https://social-auto-r8we.onrender.com/api/facebook/add-account', { cookies: parsedCookies }, config);
            setSnackbar({ open: true, message: '✅ تم إضافة حساب Facebook بنجاح!', severity: 'success' });
            setFacebookForm({ cookies: '' });
            dispatch(getFacebookAccounts());
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || 'فشل إضافة الحساب', severity: 'error' });
        }
    };

    const handleAddTelegram = async () => {
        if (!telegramForm.botToken) {
            setSnackbar({ open: true, message: 'يرجى إدخال Bot Token', severity: 'error' });
            return;
        }

        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.post('https://social-auto-r8we.onrender.com/api/telegram/add-account', telegramForm, config);
            setSnackbar({ open: true, message: '✅ تم إضافة حساب Telegram بنجاح!', severity: 'success' });
            setTelegramForm({ botToken: '', phoneNumber: '' });
            dispatch(getTelegramAccounts());
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || 'فشل إضافة الحساب', severity: 'error' });
        }
    };

    const handleAddWhatsApp = async () => {
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const response = await axios.post('https://social-auto-r8we.onrender.com/api/whatsapp/accounts',
                whatsappForm,
                config
            );
            const accountId = response.data.account._id;
            setSnackbar({ open: true, message: '✅ جاري إنشاء الحساب... امسح QR', severity: 'success' });
            setWhatsappForm({ phoneNumber: '' });
            dispatch(getWhatsAppAccounts());

            // Open QR dialog
            setQrDialog({ open: true, qrCode: null, accountId });

            // Setup socket listener for this account's QR
            const socket = io('https://social-auto-r8we.onrender.com');
            socket.on(`whatsapp_qr_${accountId}`, (qr) => {
                setQrDialog(prev => ({ ...prev, qrCode: qr }));
            });
            socket.on(`whatsapp_ready_${accountId}`, () => {
                setQrDialog({ open: false, qrCode: null, accountId: null });
                setSnackbar({ open: true, message: '✅ تم الربط بنجاح!', severity: 'success' });
                dispatch(getWhatsAppAccounts());
                socket.disconnect();
            });
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || 'فشل إضافة الحساب', severity: 'error' });
        }
    };

    const handleDeleteAccount = async () => {
        const { platform, accountId } = deleteDialog;
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };

            // Fix route path for different platforms
            let endpoint;
            if (platform === 'whatsapp') {
                endpoint = `https://social-auto-r8we.onrender.com/api/${platform}/accounts/${accountId}`;
            } else {
                endpoint = `https://social-auto-r8we.onrender.com/api/${platform}/account/${accountId}`;
            }

            await axios.delete(endpoint, config);
            setSnackbar({ open: true, message: '✅ تم حذف الحساب بنجاح!', severity: 'success' });
            setDeleteDialog({ open: false, platform: null, accountId: null });

            // Refresh accounts
            if (platform === 'instagram') dispatch(getInstagramAccounts());
            else if (platform === 'facebook') dispatch(getFacebookAccounts());
            else if (platform === 'telegram') dispatch(getTelegramAccounts());
            else if (platform === 'whatsapp') dispatch(getWhatsAppAccounts());
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || 'فشل حذف الحساب', severity: 'error' });
        }
    };

    const renderAccountsList = (accounts, platform, icon) => (
        <List>
            {accounts.length === 0 ? (
                <Alert severity="info">لا توجد حسابات مضافة</Alert>
            ) : (
                accounts.map((account) => (
                    <React.Fragment key={account._id}>
                        <ListItem>
                            <Box sx={{ mr: 2 }}>{icon}</Box>
                            <ListItemText
                                primary={account.username || account.number || account.phoneNumber || account.botToken?.slice(0, 20) + '...'}
                                secondary={
                                    <>
                                        <Chip
                                            size="small"
                                            label={account.status || 'connected'}
                                            color={account.status === 'connected' ? 'success' : 'error'}
                                            icon={account.status === 'connected' ? <CheckCircle /> : <ErrorIcon />}
                                            sx={{ mr: 1 }}
                                        />
                                        <Typography component="span" variant="caption" color="text.secondary">
                                            آخر نشاط: {new Date(account.lastActivityAt || account.lastConnected || account.createdAt).toLocaleDateString('ar')}
                                        </Typography>
                                    </>
                                }
                                secondaryTypographyProps={{ component: 'div' }}
                            />
                            <ListItemSecondaryAction>
                                <IconButton
                                    edge="end"
                                    onClick={() => setDeleteDialog({ open: true, platform, accountId: account._id })}
                                    color="error"
                                >
                                    <Delete />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                        <Divider />
                    </React.Fragment>
                ))
            )}
        </List>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 3, p: 4, mb: 3, color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AccountCircle sx={{ fontSize: 40 }} />
                    <Box>
                        <Typography variant="h4" fontWeight="bold">إدارة الحسابات</Typography>
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                            إضافة وحذف حسابات جميع المنصات من مكان واحد
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                        <Tabs
                            value={currentTab}
                            onChange={(e, newValue) => setCurrentTab(newValue)}
                            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                        >
                            <Tab icon={<Instagram />} label="Instagram" />
                            <Tab icon={<Facebook />} label="Facebook" />
                            <Tab icon={<Telegram />} label="Telegram" />
                            <Tab icon={<WhatsApp />} label="WhatsApp" />
                        </Tabs>

                        <CardContent sx={{ p: 4 }}>
                            {/* Instagram Tab */}
                            {currentTab === 0 && (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h6" gutterBottom>إضافة حساب Instagram</Typography>
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            ⚠️ استخدم حساب مخصص للأتمتة - كلمة المرور مشفرة
                                        </Alert>
                                        <TextField
                                            fullWidth
                                            label="اسم المستخدم"
                                            value={instagramForm.username}
                                            onChange={(e) => setInstagramForm({ ...instagramForm, username: e.target.value })}
                                            sx={{ mb: 2 }}
                                        />
                                        <TextField
                                            fullWidth
                                            type="password"
                                            label="كلمة المرور"
                                            value={instagramForm.password}
                                            onChange={(e) => setInstagramForm({ ...instagramForm, password: e.target.value })}
                                            sx={{ mb: 2 }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleAddInstagram}
                                            sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}
                                        >
                                            إضافة حساب
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h6" gutterBottom>الحسابات المضافة ({instagramAccounts.length})</Typography>
                                        {renderAccountsList(instagramAccounts, 'instagram', <Instagram color="error" />)}
                                    </Grid>
                                </Grid>
                            )}

                            {/* Facebook Tab */}
                            {currentTab === 1 && (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h6" gutterBottom>إضافة حساب Facebook</Typography>
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            استخدم ملحق المتصفح لتصدير Cookies (مثل EditThisCookie)
                                        </Alert>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={6}
                                            label="Cookies (JSON)"
                                            value={facebookForm.cookies}
                                            onChange={(e) => setFacebookForm({ cookies: e.target.value })}
                                            placeholder='[{"name":"c_user","value":"..."}]'
                                            sx={{ mb: 2 }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleAddFacebook}
                                            sx={{ background: 'linear-gradient(135deg, #3b5998 0%, #192f5d 100%)' }}
                                        >
                                            إضافة حساب
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h6" gutterBottom>الحسابات المضافة ({facebookAccounts.length})</Typography>
                                        {renderAccountsList(facebookAccounts, 'facebook', <Facebook color="primary" />)}
                                    </Grid>
                                </Grid>
                            )}

                            {/* Telegram Tab */}
                            {currentTab === 2 && (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h6" gutterBottom>إضافة حساب Telegram</Typography>
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            احصل على Bot Token من @BotFather
                                        </Alert>
                                        <TextField
                                            fullWidth
                                            label="Bot Token"
                                            value={telegramForm.botToken}
                                            onChange={(e) => setTelegramForm({ ...telegramForm, botToken: e.target.value })}
                                            placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                                            sx={{ mb: 2 }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="رقم الهاتف (اختياري)"
                                            value={telegramForm.phoneNumber}
                                            onChange={(e) => setTelegramForm({ ...telegramForm, phoneNumber: e.target.value })}
                                            sx={{ mb: 2 }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleAddTelegram}
                                            sx={{ background: 'linear-gradient(135deg, #0088cc 0%, #005580 100%)' }}
                                        >
                                            إضافة حساب
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h6" gutterBottom>الحسابات المضافة ({telegramAccounts.length})</Typography>
                                        {renderAccountsList(telegramAccounts, 'telegram', <Telegram color="info" />)}
                                    </Grid>
                                </Grid>
                            )}

                            {/* WhatsApp Tab */}
                            {currentTab === 3 && (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h6" gutterBottom>إضافة حساب WhatsApp</Typography>
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            📱 ستحتاج لمسح QR Code من تطبيق WhatsApp
                                        </Alert>
                                        <TextField
                                            fullWidth
                                            label="رقم الهاتف (اختياري)"
                                            placeholder="+962791234567"
                                            value={whatsappForm.phoneNumber}
                                            onChange={(e) => setWhatsappForm({ phoneNumber: e.target.value })}
                                            sx={{ mb: 2 }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleAddWhatsApp}
                                            sx={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
                                        >
                                            إضافة حساب
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h6" gutterBottom>الحسابات المضافة ({whatsappAccounts.length})</Typography>
                                        {renderAccountsList(whatsappAccounts, 'whatsapp', <WhatsApp color="success" />)}
                                    </Grid>
                                </Grid>
                            )}
                        </CardContent>
                    </Card>
                </Grid >
            </Grid >

            {/* QR Code Dialog */}
            <Dialog open={qrDialog.open} onClose={() => setQrDialog({ open: false, qrCode: null, accountId: null })}>
                <DialogTitle>مسح QR Code</DialogTitle>
                <DialogContent>
                    <Box sx={{ textAlign: 'center', p: 3 }}>
                        {qrDialog.qrCode ? (
                            <>
                                <Typography variant="body1" gutterBottom>
                                    افتح WhatsApp على هاتفك وامسح الكود:
                                </Typography>
                                <Box sx={{ display: 'inline-block', p: 2, bgcolor: 'white', borderRadius: 2, mt: 2 }}>
                                    <img
                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qrDialog.qrCode)}`}
                                        alt="QR Code"
                                        style={{ width: 250, height: 250 }}
                                    />
                                </Box>
                                <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
                                    الهاتف → الإعدادات → الأجهزة المرتبطة → ربط جهاز
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Typography variant="body1" gutterBottom>جاري إنشاء QR Code...</Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <div className="spinner"></div>
                                </Box>
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setQrDialog({ open: false, qrCode: null, accountId: null })}>
                        إغلاق
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            < Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, platform: null, accountId: null })}>
                <DialogTitle>تأكيد الحذف</DialogTitle>
                <DialogContent>
                    <Typography>هل أنت متأكد من حذف هذا الحساب؟</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, platform: null, accountId: null })}>إلغاء</Button>
                    <Button onClick={handleDeleteAccount} color="error" variant="contained">حذف</Button>
                </DialogActions>
            </Dialog >

            {/* Snackbar */}
            < Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar >
        </Box >
    );
};

export default AccountsManager;
